import React, { useState } from 'react';
import { GiRadioactive } from "react-icons/gi";
import VisDiv from './VisDiv';
import { useStateContext } from '../../context/StateContext';
import { isGodMode } from '../../lib/userPermissions';
import { HiOutlineQuestionMarkCircle } from "react-icons/hi2";
import { IoIosInformationCircleOutline } from "react-icons/io";
import Swal from 'sweetalert2';

/**
 * A component that renders a test button.
 *  #core
 * The button is only visible if the user is in god mode or if hideTestButtons is false.
 * 
 * The button can be clicked, and will call the given onClick function when clicked.
 * 
 * The component can also be given a copyId, which if set, will copy the given id to the clipboard when clicked.
 * 
 * The component can also be given a message, which will be displayed in a tooltip when the button is hovered over.
 * 
 * The component can also be given a buttonId, which will be passed to the onClick function when clicked.
 * 
 * The component can also be given a size, which will be used to set the width and height of the button.
 * 
 * @example
 * <TestButton
 *  onClick={() => console.log("Button was clicked")}
 *  copyId="1234"
 *  size={50}
 *  message="Click me!"
 *  buttonId="my-button"
 * />
 */
const TestButton = ({
  style,
  onClick = () => {},
  consoleData = {},
  copyId,
  size = 30,
  message = "",
  buttonId = ""
}) => {
  const { sUser, hideTestButtons } = useStateContext();
  const [isHovered, setIsHovered] = useState(false);

  const testFunction = (e) => {
    e?.stopPropagation?.();
    if (copyId) {
      navigator.clipboard.writeText(copyId);
    }
    onClick({ buttonId, console });
    console.log(buttonId, message, consoleData);
  };
    if(!isGodMode(sUser) || hideTestButtons) return null
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <div
        onClick={testFunction}
        onMouseEnter={(e) => {
          e?.stopPropagation?.();
          setIsHovered(true);
        }}
        onMouseLeave={(e) => {
          e?.stopPropagation?.();
          setIsHovered(false);
        }}
        rules={[isGodMode(sUser)]}
        style={{
          backgroundColor: "black",
          borderRadius: "100%",
          cursor: "pointer",
          margin: "10px",
          width: `${size}px`,
          height: `${size}px`,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          ...style
        }}
      >
        <GiRadioactive size={"100%"} color={copyId ? "orange" : "yellow"} />
      </div>

      {isHovered && message && (
        <div
          style={{
            position: "absolute",
            bottom: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginBottom: "1px",
            backgroundColor: "black",
            color: "white",
            padding: "5px 5px",
            borderRadius: "4px",
            fontSize: "12px",
            whiteSpace: "nowrap",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          {message}
        </div>
      )}
    </div>
  );
};

export default TestButton;


/**
 * A component that renders a question mark button.
 * 
 * The button is only visible if hideTestButtons is false.
 * 
 * The button can be clicked, and will call the given onClick function when clicked.
 * 
 * The component can also be given a copyId, which if set, will copy the given id to the clipboard when clicked.
 * 
 * The component can also be given a message, which will be displayed in a tooltip when the button is hovered over.
 * 
 * The component can also be given a size, which will be used to set the width and height of the button.
 * 
 * @example
 * <QuestionMarkButton
 *  onClick={() => console.log("Button was clicked")}
 *  copyId="1234"
 *  size={50}
 *  message="Click me!"
 * />
 */
export const QuestionMarkButton = ({
  style,
  onClick = () => {},
  size = 30,
  message = "",
}) => {
  const { sUser, hideTestButtons } = useStateContext();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <div
        onClick={onClick}
        onMouseEnter={(e) => {
          e?.stopPropagation?.();
          setIsHovered(true);
        }}
        onMouseLeave={(e) => {
          e?.stopPropagation?.();
          setIsHovered(false);
        }}
        rules={[]}
        style={{
          backgroundColor: "black",
          borderRadius: "100%",
          cursor: "pointer",
          margin: "10px",
          width: `${size}px`,
          height: `${size}px`,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          ...style
        }}
      >
        <HiOutlineQuestionMarkCircle color={"white"} size={"100%"} />
      </div>

      {isHovered && message && (
        <div
          style={{
            position: "absolute",
            bottom: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginBottom: "1px",
            backgroundColor: "black",
            color: "white",
            padding: "5px 5px",
            borderRadius: "4px",
            fontSize: "12px",
            whiteSpace: "nowrap",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          {message}
        </div>
      )}
    </div>
  );
};

/**
 * A component that renders an information circle button.
 * 
 * The button is only visible if hideTestButtons is false.
 * 
 * The button can be clicked, and will call the given showMessage function when clicked.
 * 
 * The component can also be given a title, which will be used as the title of the Swal popup.
 * 
 * The component can also be given a buttonId, which will be logged to the console when the button is clicked.
 * 
 * The component can also be given a consoleData object, which will be logged to the console when the button is clicked.
 * 
 * The component can also be given an html string, which will be used as the html of the Swal popup.
 * 
 * The component can also be given a size, which will be used to set the width and height of the button.
 * 
 * @example
 * <InfoButton
 *  onClick={() => console.log("Button was clicked")}
 *  title="Test button"
 *  buttonId="1234"
 *  size={50}
 *  html="<p>This is a test button.</p>"
 * />
 */
export const InfoButton = ({
  style,
  size = 30,
  title = "",
  buttonId = "",
  consoleData = {},

  html,
}) => {
  const { sUser, hideTestButtons } = useStateContext();
 const showMessage = () => {
  console.log(buttonId, consoleData)
  Swal?.fire({
      position: 'center',
      icon: 'info',
      title: title,
      html: html,
      width: "80%",
      showConfirmButton: true,
      //timer: 1500
    })
 }

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <div
        onClick={showMessage}
        rules={[]}
        style={{
          //backgroundColor: "black",
          borderRadius: "100%",
          cursor: "pointer",
          width: `${size}px`,
          height: `${size}px`,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          ...style
        }}
      >
        <IoIosInformationCircleOutline color={"#324d67"} size={"100%"} />
      </div>
    </div>
  );
};